/**
 * ManualAddForm (ADR 0004). Inline form at the bottom of a Rollup Detail
 * section to add a Labor / Equipment / Material line the foreman PDF didn't
 * carry. The Day picker is constrained to the rollup's Sun–Sat window — a
 * report_date outside the week would be silently dropped from the xlsx export,
 * so there's no free-form date entry and no defaulting to today.
 */
import { useEffect, useMemo, useState } from 'react';
import type { SettingsEmployee, SettingsEquipment } from '@coffman-tm/shared';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { MoneyInput } from './MoneyInput';
import { Dropdown } from './design';

type Section = 'labor' | 'equipment' | 'material' | 'subcontractor';

const REASON_MIN = 10;
const REASON_MAX = 280;
const OTHER = '__other__';
/** Mirrors the server's SUBCONTRACTOR_INVOICE_MAX_BYTES — checked here too so
 *  the PM learns a 40 MB scan is too big before uploading it. */
const INVOICE_MAX_BYTES = 10 * 1024 * 1024;

export interface ManualAddFormProps {
  section: Section;
  pcoId: string;
  weekEndingDate: string;
  /** Canonical rollup id from the detail page. When present the add targets
   *  this exact rollup (split-week safe — the row pins to it instead of
   *  sweeping to the default). Null/omitted falls back to the legacy
   *  (pco, week) endpoints. */
  rollupId?: string | null;
  onClose: () => void;
  onAdded: () => Promise<unknown>;
}

/** The 7 days (Sun..Sat) of the rollup week, as picker options. Exported for
 *  the Subcontractors section's inline date select on RollupDetailPage. */
export function weekDays(weekEndingYmd: string): { value: string; label: string }[] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekEndingYmd);
  if (!m) return [];
  const sat = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const out: { value: string; label: string }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(sat);
    d.setUTCDate(sat.getUTCDate() - i);
    const y = d.getUTCFullYear();
    const mo = d.getUTCMonth() + 1;
    const da = d.getUTCDate();
    out.push({
      value: `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`,
      label: `${dow[d.getUTCDay()]} ${mo}/${da}`,
    });
  }
  return out;
}

export function ManualAddForm({
  section,
  pcoId,
  weekEndingDate,
  rollupId,
  onClose,
  onAdded,
}: ManualAddFormProps) {
  const { token } = useAuth();
  const [employees, setEmployees] = useState<SettingsEmployee[]>([]);
  const [equipment, setEquipment] = useState<SettingsEquipment[]>([]);

  // Subcontractor only: the vendor invoice PDF, uploaded as a SECOND call
  // after the line exists (see the submit handler) so a failed upload never
  // costs the PM the line itself.
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceWarning, setInvoiceWarning] = useState<string | null>(null);

  const [entityId, setEntityId] = useState<string>(''); // employee_id or equipment_id, '' or OTHER
  const [freeText, setFreeText] = useState<string>(''); // free-text name / asset id / material or sub desc
  const [unit, setUnit] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [amount, setAmount] = useState<string>(''); // hours or quantity
  const [rateDollars, setRateDollars] = useState<string>('');
  // Subcontractor lump amount — MoneyInput raw decimal string ("683814.00").
  const [subAmount, setSubAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const days = useMemo(() => weekDays(weekEndingDate), [weekEndingDate]);

  // Roster picker options (labor/equipment only) — the free "— Other —" tail
  // lets a name/asset id not yet in Settings still get billed this week.
  const entityOptions = useMemo(
    () => [
      { value: '', label: 'Select…' },
      ...(section === 'labor'
        ? employees.map((e) => ({ value: e.id, label: `${e.name} (${e.classification})` }))
        : equipment.map((e) => ({ value: e.id, label: `${e.asset_id} — ${e.description}` }))),
      { value: OTHER, label: '— Other (type a name) —' },
    ],
    [section, employees, equipment],
  );

  const dayOptions = useMemo(
    () => [{ value: '', label: 'Select' }, ...days.map((d) => ({ value: d.value, label: d.label }))],
    [days],
  );

  // Load roster for the typeahead (labor/equipment only).
  useEffect(() => {
    if (!token) return;
    if (section === 'labor') {
      void api.settings.employees(token).then(setEmployees).catch(() => {});
    } else if (section === 'equipment') {
      void api.settings.equipment(token).then(setEquipment).catch(() => {});
    }
  }, [token, section]);

  // Prefill rate from the selected roster entity.
  useEffect(() => {
    if (entityId === '' || entityId === OTHER) return;
    if (section === 'labor') {
      const emp = employees.find((e) => e.id === entityId);
      if (emp) setRateDollars((emp.st_rate_cents / 100).toFixed(2));
    } else if (section === 'equipment') {
      const eq = equipment.find((e) => e.id === entityId);
      if (eq) setRateDollars((eq.hourly_rate_cents / 100).toFixed(2));
    }
  }, [entityId, section, employees, equipment]);

  // Mounted only while open, so the lock is unconditional. Gating on `busy`
  // (as the keydown effect must) would churn the lock on every save.
  useBodyScrollLock(true);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const selectedEmp = section === 'labor' ? employees.find((e) => e.id === entityId) : undefined;

  const trimmedReason = reason.trim();
  const amountNum = Number(amount);
  const rateNum = Number(rateDollars);
  const subAmountCents = Math.round(Number(subAmount || '0') * 100);
  const entityValid =
    section === 'material' || section === 'subcontractor'
      ? freeText.trim() !== ''
      : entityId === OTHER
        ? freeText.trim() !== ''
        : entityId !== '';
  // Subcontractor: one lump Amount (any non-zero figure — a negative one is a
  // vendor credit, entered as its own line with its own document) and an
  // OPTIONAL reason — unlike the manual-add sections, where the 10-char reason
  // is required.
  const valid =
    section === 'subcontractor'
      ? entityValid &&
        day !== '' &&
        Number.isFinite(subAmountCents) &&
        subAmountCents !== 0 &&
        trimmedReason.length <= REASON_MAX &&
        !busy
      : entityValid &&
        day !== '' &&
        Number.isFinite(amountNum) &&
        amountNum > 0 &&
        Number.isFinite(rateNum) &&
        rateNum >= 0 &&
        trimmedReason.length >= REASON_MIN &&
        trimmedReason.length <= REASON_MAX &&
        !busy;

  const handleSave = async () => {
    if (!token || !valid) return;
    setBusy(true);
    setErr(null);
    const rateCents = Math.round(rateNum * 100);
    try {
      // Canonical addressing when the page holds a rollup id (split-week safe:
      // the row lands in — and pins to — the addressed rollup); legacy
      // (pco, week) otherwise.
      if (section === 'labor') {
        const body = {
          employee_id: entityId === OTHER ? null : entityId,
          employee_name: entityId === OTHER ? freeText.trim() : undefined,
          report_date: day,
          total_hours: amountNum,
          rate_cents: rateCents,
          reason: trimmedReason,
        };
        if (rollupId) await api.rollups.manualAddLaborById(token, rollupId, body);
        else await api.rollups.manualAddLabor(token, pcoId, weekEndingDate, body);
      } else if (section === 'equipment') {
        const body = {
          equipment_id: entityId === OTHER ? null : entityId,
          asset_id: entityId === OTHER ? freeText.trim() : undefined,
          report_date: day,
          hours: amountNum,
          rate_cents: rateCents,
          reason: trimmedReason,
        };
        if (rollupId) await api.rollups.manualAddEquipmentById(token, rollupId, body);
        else await api.rollups.manualAddEquipment(token, pcoId, weekEndingDate, body);
      } else if (section === 'subcontractor') {
        // Canonical-only endpoint — the header action is gated on a persisted
        // rollup id, so this branch can't be reached without one.
        if (!rollupId) throw new Error('This week has no rollup yet — save a block first.');
        const created = await api.rollups.addSubcontractor(token, rollupId, {
          description: freeText.trim(),
          amount_cents: subAmountCents,
          invoice_date: day,
          reason: trimmedReason || undefined,
        });
        // Two calls on purpose: the line is created first, so if the upload
        // fails the PM keeps the line (and its money) and can retry the
        // attachment from the row's own attach action. Failing the whole add
        // over a flaky upload would be the worse trade.
        if (invoiceFile) {
          try {
            await api.rollups.uploadSubcontractorInvoice(
              token,
              rollupId,
              created.line.id,
              invoiceFile,
            );
          } catch (uploadErr) {
            await onAdded();
            setBusy(false);
            setErr(
              `Line added, but the invoice PDF did not upload (${
                uploadErr instanceof Error ? uploadErr.message : 'upload failed'
              }). Use the paperclip button on the line to retry.`,
            );
            return;
          }
        }
      } else {
        const body = {
          quoted_text: freeText.trim(),
          unit: unit.trim() || undefined,
          report_date: day,
          quantity: amountNum,
          rate_cents: rateCents,
          reason: trimmedReason,
        };
        if (rollupId) await api.rollups.manualAddMaterialById(token, rollupId, body);
        else await api.rollups.manualAddMaterial(token, pcoId, weekEndingDate, body);
      }
      await onAdded();
      onClose();
    } catch (e) {
      if (
        e instanceof ApiCallError &&
        // Only an HTTP failure carries a server error code worth showing. A
        // timeout or network drop has a synthetic payload ('timeout',
        // 'network_error') and a written-for-humans `message` — showing the
        // code there would put the literal word "timeout" in front of the PM.
        e.kind === 'http' &&
        e.payload &&
        typeof e.payload === 'object' &&
        'error' in e.payload
      ) {
        setErr(String((e.payload as { error?: unknown }).error));
      } else {
        setErr(e instanceof Error ? e.message : 'Add failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const amountLabel = section === 'material' ? 'Quantity' : 'Total hrs';
  const entityLabel =
    section === 'labor' ? 'Employee' : section === 'equipment' ? 'Equipment' : 'Description';
  const title =
    section === 'material'
      ? 'Add a material line'
      : section === 'subcontractor'
        ? 'Add a subcontractor line'
        : `Add ${section} hours`;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">{title}</div>
        </div>
        <div className="confirm-modal-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        {/* Entity */}
        <Field label={entityLabel}>
          {section === 'material' || section === 'subcontractor' ? (
            <input
              style={inputStyle}
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder={
                section === 'subcontractor'
                  ? 'e.g. Brix Paving - Pervious Pavement'
                  : 'e.g. Roadbase 3/4"'
              }
              disabled={busy}
            />
          ) : (
            <Dropdown options={entityOptions} value={entityId} onChange={setEntityId} disabled={busy} />
          )}
        </Field>

        {section !== 'material' && entityId === OTHER && (
          <Field label="Name">
            <input
              style={inputStyle}
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder={section === 'labor' ? 'Employee name' : 'Asset ID'}
              disabled={busy}
            />
          </Field>
        )}

        {section === 'labor' && selectedEmp && (
          <Field label="Class">
            <span style={{ fontSize: 13, paddingBottom: 6, color: 'var(--ink-3)' }}>
              {selectedEmp.classification}
            </span>
          </Field>
        )}

        {section === 'material' && (
          <Field label="Unit">
            <input
              style={{ ...inputStyle, width: 70 }}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="ea"
              disabled={busy}
            />
          </Field>
        )}

        <Field label="Day">
          <Dropdown
            style={{ width: 110 }}
            options={dayOptions}
            value={day}
            onChange={setDay}
            disabled={busy}
          />
        </Field>

        {section === 'subcontractor' ? (
          // One lump amount — the vendor invoice total, modeled as qty 1 ×
          // amount on the invoice date. MoneyInput (not the plain rate input)
          // because these are large figures where comma grouping matters; it
          // also keeps a leading minus, which is how a vendor credit is typed.
          <Field label="Amount ($)">
            <MoneyInput
              value={subAmount}
              onChange={setSubAmount}
              disabled={busy}
              style={{ ...inputStyle, width: 130 }}
            />
          </Field>
        ) : (
          <>
            <Field label={amountLabel}>
              <input
                style={{ ...inputStyle, width: 80 }}
                type="number"
                min="0"
                step="0.25"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={busy}
              />
            </Field>

            <Field label="Rate ($)">
              <input
                style={{ ...inputStyle, width: 90 }}
                type="number"
                min="0"
                step="0.01"
                value={rateDollars}
                onChange={(e) => setRateDollars(e.target.value)}
                disabled={busy}
              />
            </Field>
          </>
        )}
      </div>

      {section === 'subcontractor' && (
        // Optional at add time: Coffman sometimes bills the lump before the
        // vendor's paperwork lands. The row's own action attaches it later.
        <div style={{ marginTop: 10 }}>
          <Field label="Vendor invoice PDF (optional)">
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setInvoiceWarning(null);
                if (f && f.size > INVOICE_MAX_BYTES) {
                  setInvoiceFile(null);
                  e.target.value = '';
                  setInvoiceWarning(
                    `That PDF is ${(f.size / 1024 / 1024).toFixed(1)} MB — the limit is 10 MB.`,
                  );
                  return;
                }
                setInvoiceFile(f);
              }}
              style={{ fontSize: 12.5 }}
            />
          </Field>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {invoiceWarning ? (
              <span style={{ color: 'var(--danger)' }}>{invoiceWarning}</span>
            ) : (
              "Sent to the GC with this week's rollup."
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <Field label={section === 'subcontractor' ? 'Reason (optional)' : 'Reason (required)'}>
          <textarea
            style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              section === 'subcontractor'
                ? 'Optional note — e.g. which vendor invoice this covers (≤ 280 chars)'
                : 'Why is this being added by hand? (10–280 chars)'
            }
            disabled={busy}
          />
        </Field>
        <div className="muted" style={{ fontSize: 11, textAlign: 'right' }}>
          {trimmedReason.length}/{REASON_MAX}
        </div>
      </div>

      {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-cta" onClick={handleSave} disabled={!valid}>
            {busy ? 'Adding…' : 'Add line'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
};
